import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import type { FlowchartData, FlowchartNode, FlowchartEdge, NodeColor } from "@/lib/chat/parseFlowchartBlock";

const NODE_COLORS: Record<NodeColor, { fill: string; stroke: string }> = {
  blue: { fill: "#dbeafe", stroke: "#93c5fd" },
  green: { fill: "#d1fae5", stroke: "#6ee7b7" },
  purple: { fill: "#ede9fe", stroke: "#c4b5fd" },
  orange: { fill: "#ffedd5", stroke: "#fdba74" },
  yellow: { fill: "#fef9c3", stroke: "#fde047" },
};

const RECT_W = 160;
const RECT_H_BASE = 40;
const DIAMOND_SIZE = 50;
const V_GAP = 100;
const H_GAP = 180;

// ---------- Layout computation ----------

interface PositionedNode extends FlowchartNode {
  x: number;
  y: number;
}

function computeLayout(
  nodes: FlowchartNode[],
  edges: FlowchartEdge[]
): { positioned: PositionedNode[]; width: number; height: number } {
  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const inDegree = new Map<string, number>();
  const children = new Map<string, FlowchartEdge[]>();

  for (const n of nodes) {
    inDegree.set(n.id, 0);
    children.set(n.id, []);
  }
  for (const e of edges) {
    inDegree.set(e.to, (inDegree.get(e.to) ?? 0) + 1);
    children.get(e.from)?.push(e);
  }

  // Topological sort
  const queue: string[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }
  const order: string[] = [];
  const visited = new Set<string>();
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    order.push(id);
    for (const e of children.get(id) ?? []) {
      const newDeg = (inDegree.get(e.to) ?? 1) - 1;
      inDegree.set(e.to, newDeg);
      if (newDeg <= 0 && !visited.has(e.to)) queue.push(e.to);
    }
  }
  // Add any remaining nodes not reached
  for (const n of nodes) {
    if (!visited.has(n.id)) order.push(n.id);
  }

  // Assign positions: center column, with branching for diamonds
  const CENTER_X = 350;
  const positions = new Map<string, { x: number; y: number }>();
  let currentY = 60;
  const branchColumns = new Map<string, number>(); // node id -> column offset

  for (const id of order) {
    const node = nodeMap.get(id)!;
    if (positions.has(id)) continue;

    const x = branchColumns.get(id) ?? CENTER_X;
    positions.set(id, { x, y: currentY });

    // If diamond with 2+ outgoing edges, branch
    const outEdges = children.get(id) ?? [];
    if (node.shape === "diamond" && outEdges.length >= 2) {
      const yesEdge = outEdges.find((e) => e.label?.toLowerCase().includes("yes"));
      const noEdge = outEdges.find((e) => e.label?.toLowerCase().includes("no"));
      if (yesEdge && noEdge) {
        // "Yes" goes down-left, "No" goes down-right
        const branchY = currentY + V_GAP;
        const leftX = x - H_GAP;
        const rightX = x + H_GAP;
        positions.set(yesEdge.to, { x: leftX, y: branchY });
        positions.set(noEdge.to, { x: rightX, y: branchY });
        branchColumns.set(yesEdge.to, leftX);
        branchColumns.set(noEdge.to, rightX);
      }
    }

    currentY += V_GAP;
  }

  // Build positioned nodes
  const positioned: PositionedNode[] = nodes.map((n) => {
    const pos = positions.get(n.id) ?? { x: CENTER_X, y: 60 };
    return { ...n, x: pos.x, y: pos.y };
  });

  // Compute SVG bounds
  let maxX = 0;
  let maxY = 0;
  for (const p of positioned) {
    const right = p.shape === "diamond" ? p.x + DIAMOND_SIZE + 20 : p.x + RECT_W / 2 + 20;
    const bottom = p.shape === "diamond" ? p.y + DIAMOND_SIZE + 20 : p.y + (RECT_H_BASE + (p.label.length - 1) * 16) / 2 + 20;
    if (right > maxX) maxX = right;
    if (bottom > maxY) maxY = bottom;
  }

  return {
    positioned,
    width: Math.max(maxX + 40, 700),
    height: maxY + 40,
  };
}

// ---------- SVG sub-components ----------

function getNodeBottom(node: PositionedNode) {
  if (node.shape === "diamond") return { x: node.x, y: node.y + DIAMOND_SIZE };
  const h = RECT_H_BASE + (node.label.length - 1) * 16;
  return { x: node.x, y: node.y + h / 2 };
}

function getNodeTop(node: PositionedNode) {
  if (node.shape === "diamond") return { x: node.x, y: node.y - DIAMOND_SIZE };
  const h = RECT_H_BASE + (node.label.length - 1) * 16;
  return { x: node.x, y: node.y - h / 2 };
}

function getNodeLeft(node: PositionedNode) {
  if (node.shape === "diamond") return { x: node.x - DIAMOND_SIZE, y: node.y };
  return { x: node.x - RECT_W / 2, y: node.y };
}

function getNodeRight(node: PositionedNode) {
  if (node.shape === "diamond") return { x: node.x + DIAMOND_SIZE, y: node.y };
  return { x: node.x + RECT_W / 2, y: node.y };
}

function RectNodeSVG({ node }: { node: PositionedNode }) {
  const colors = NODE_COLORS[node.color];
  const h = RECT_H_BASE + (node.label.length - 1) * 16;
  return (
    <g>
      <rect
        x={node.x - RECT_W / 2}
        y={node.y - h / 2}
        width={RECT_W}
        height={h}
        rx={8}
        ry={8}
        fill={colors.fill}
        stroke={colors.stroke}
        strokeWidth={1.5}
      />
      {node.label.map((line, i) => (
        <text
          key={i}
          x={node.x}
          y={node.y + (i - (node.label.length - 1) / 2) * 16}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={11}
          fontFamily="system-ui, sans-serif"
          fill="#374151"
        >
          {line}
        </text>
      ))}
    </g>
  );
}

function DiamondNodeSVG({ node }: { node: PositionedNode }) {
  const colors = NODE_COLORS[node.color];
  const s = DIAMOND_SIZE;
  const points = `${node.x},${node.y - s} ${node.x + s},${node.y} ${node.x},${node.y + s} ${node.x - s},${node.y}`;
  return (
    <g>
      <polygon points={points} fill={colors.fill} stroke={colors.stroke} strokeWidth={1.5} />
      {node.label.map((line, i) => (
        <text
          key={i}
          x={node.x}
          y={node.y + (i - (node.label.length - 1) / 2) * 14}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={10}
          fontFamily="system-ui, sans-serif"
          fill="#374151"
        >
          {line}
        </text>
      ))}
    </g>
  );
}

function EdgeLineSVG({
  edge,
  nodeMap,
}: {
  edge: FlowchartEdge;
  nodeMap: Map<string, PositionedNode>;
}) {
  const fromNode = nodeMap.get(edge.from);
  const toNode = nodeMap.get(edge.to);
  if (!fromNode || !toNode) return null;

  const sameColumn = Math.abs(fromNode.x - toNode.x) < 10;
  let path: string;
  let labelPos: { x: number; y: number };

  if (sameColumn) {
    const start = getNodeBottom(fromNode);
    const end = getNodeTop(toNode);
    path = `M${start.x},${start.y} L${end.x},${end.y}`;
    labelPos = { x: (start.x + end.x) / 2 + 10, y: (start.y + end.y) / 2 };
  } else if (toNode.y > fromNode.y) {
    // Different column, going down: step path
    const start = getNodeBottom(fromNode);
    const end = getNodeTop(toNode);
    const midY = start.y + 20;
    path = `M${start.x},${start.y} L${start.x},${midY} L${end.x},${midY} L${end.x},${end.y}`;
    labelPos = { x: (start.x + end.x) / 2, y: midY - 8 };
  } else {
    // Backwards edge: go around the side
    const start = getNodeRight(fromNode);
    const end = getNodeRight(toNode);
    const midX = Math.max(start.x, end.x) + 50;
    path = `M${start.x},${start.y} L${midX},${start.y} L${midX},${end.y} L${end.x},${end.y}`;
    labelPos = { x: midX + 5, y: (start.y + end.y) / 2 };
  }

  return (
    <g>
      <path
        d={path}
        fill="none"
        stroke="#d1d5db"
        strokeWidth={1.2}
        markerEnd="url(#fc-arrowhead)"
      />
      {edge.label && (
        <text
          x={labelPos.x}
          y={labelPos.y}
          textAnchor="middle"
          fontSize={9}
          fontFamily="system-ui, sans-serif"
          fill="#9ca3af"
        >
          {edge.label.split("\n").map((line, i) => (
            <tspan key={i} x={labelPos.x} dy={i === 0 ? 0 : 12}>
              {line}
            </tspan>
          ))}
        </text>
      )}
    </g>
  );
}

// ---------- Zoom/Pan Canvas ----------

function ZoomPanCanvas({
  children,
  svgWidth,
  svgHeight,
  style,
  className,
}: {
  children: React.ReactNode;
  svgWidth: number;
  svgHeight: number;
  style?: React.CSSProperties;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const [showHint, setShowHint] = useState(true);

  const hideHint = useCallback(() => setShowHint(false), []);

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const rect = el.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const delta = -e.deltaY * 0.003;
        const newZoom = Math.min(Math.max(zoom + delta * zoom, 0.2), 5);
        const scale = newZoom / zoom;
        setPan((p) => ({
          x: mouseX - scale * (mouseX - p.x),
          y: mouseY - scale * (mouseY - p.y),
        }));
        setZoom(newZoom);
        hideHint();
      }
    };
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [zoom, hideHint]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setDragging(true);
      dragStart.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
      hideHint();
    },
    [pan, hideHint]
  );

  useEffect(() => {
    if (!dragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      setPan({
        x: dragStart.current.panX + (e.clientX - dragStart.current.x),
        y: dragStart.current.panY + (e.clientY - dragStart.current.y),
      });
    };
    const handleMouseUp = () => setDragging(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging]);

  const touchStart = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        const t = e.touches[0];
        touchStart.current = { x: t.clientX, y: t.clientY, panX: pan.x, panY: pan.y };
        hideHint();
      }
    },
    [pan, hideHint]
  );

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      const t = e.touches[0];
      setPan({
        x: touchStart.current.panX + (t.clientX - touchStart.current.x),
        y: touchStart.current.panY + (t.clientY - touchStart.current.y),
      });
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        ...style,
        overflow: "hidden",
        position: "relative",
        cursor: dragging ? "grabbing" : "grab",
        touchAction: "none",
        userSelect: "none",
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
    >
      <div
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
          transformOrigin: "0 0",
          width: svgWidth,
          height: svgHeight,
        }}
      >
        {children}
      </div>
      {showHint && (
        <div className="pointer-events-none absolute bottom-4 right-4 flex items-center gap-1.5 rounded-lg border border-border bg-card/90 px-3 py-1.5 text-xs text-muted-foreground shadow-sm">
          <kbd className="inline-block rounded border border-border bg-muted px-1.5 py-0.5 text-[11px]">
            Ctrl
          </kbd>
          <span>+ Scroll&nbsp;&nbsp;or&nbsp;&nbsp;Pinch</span>
        </div>
      )}
    </div>
  );
}

// ---------- Public component ----------

interface FlowchartCanvasProps {
  data: FlowchartData;
  maxHeight?: string;
}

export const FlowchartCanvas: React.FC<FlowchartCanvasProps> = ({
  data,
  maxHeight = "25rem",
}) => {
  const layout = useMemo(() => computeLayout(data.nodes, data.edges), [data]);
  const nodeMap = useMemo(
    () => new Map(layout.positioned.map((n) => [n.id, n])),
    [layout]
  );

  const svgContent = (
    <svg
      viewBox={`0 0 ${layout.width} ${layout.height}`}
      width={layout.width}
      height={layout.height}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
    >
      <defs>
        <marker
          id="fc-arrowhead"
          markerWidth="8"
          markerHeight="6"
          refX="8"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 8 3, 0 6" fill="#d1d5db" />
        </marker>
      </defs>
      {data.edges.map((edge, i) => (
        <EdgeLineSVG key={i} edge={edge} nodeMap={nodeMap} />
      ))}
      {layout.positioned.map((node) =>
        node.shape === "diamond" ? (
          <DiamondNodeSVG key={node.id} node={node} />
        ) : (
          <RectNodeSVG key={node.id} node={node} />
        )
      )}
    </svg>
  );

  return (
    <ZoomPanCanvas
      svgWidth={layout.width}
      svgHeight={layout.height}
      className="border-t border-border"
      style={{
        backgroundColor: "hsl(var(--muted) / 0.2)",
        maxHeight: maxHeight === "none" ? undefined : maxHeight,
      }}
    >
      {svgContent}
    </ZoomPanCanvas>
  );
};
